import type { Accessor } from "solid-js";
import { createSignal, onCleanup } from "solid-js";
import type { BoardItemListItemResponse } from "@/client";

const PLAY_PANEL_OPEN_MS = 325;
const PLAY_PANEL_CLOSE_MS = 325;

export type BoardPlayPanel = {
	playPanelMounted: Accessor<boolean>;
	playPanelOpen: Accessor<boolean>;
	playPanelClosing: Accessor<boolean>;
	playPanelLayoutAnimating: Accessor<boolean>;
	clipToPlay: Accessor<BoardItemListItemResponse | undefined>;
	videoIndex: Accessor<number | undefined>;
	carouselInstantJumpToken: Accessor<number>;
	openPlayPanel: (clip: BoardItemListItemResponse, index: number) => void;
	setActiveVideoIndex: (index: number) => void;
	closePlayPanel: (options?: { immediate?: boolean }) => void;
	togglePlayPanel: () => void;
	isPlayPanelActive: () => boolean;
};

export function useBoardPlayPanel(): BoardPlayPanel {
	const [playPanelMounted, setPlayPanelMounted] = createSignal(false);
	const [playPanelOpen, setPlayPanelOpen] = createSignal(false);
	const [playPanelClosing, setPlayPanelClosing] = createSignal(false);
	const [playPanelLayoutAnimating, setPlayPanelLayoutAnimating] =
		createSignal(false);
	const [clipToPlay, setClipToPlay] = createSignal<
		BoardItemListItemResponse | undefined
	>(undefined);
	const [videoIndex, setVideoIndex] = createSignal<number | undefined>(
		undefined,
	);
	const [carouselInstantJumpToken, setCarouselInstantJumpToken] =
		createSignal(0);

	let playPanelCloseTimeout: ReturnType<typeof setTimeout> | undefined;
	let playPanelLayoutAnimTimeout: ReturnType<typeof setTimeout> | undefined;
	let playPanelOpenFrame: number | undefined;

	const clearPlayPanelLayoutAnimTimeout = () => {
		if (playPanelLayoutAnimTimeout !== undefined) {
			clearTimeout(playPanelLayoutAnimTimeout);
			playPanelLayoutAnimTimeout = undefined;
		}
	};

	const beginPlayPanelLayoutAnimation = () => {
		clearPlayPanelLayoutAnimTimeout();
		setPlayPanelLayoutAnimating(true);
	};

	const endPlayPanelLayoutAnimation = (ms: number) => {
		clearPlayPanelLayoutAnimTimeout();
		playPanelLayoutAnimTimeout = setTimeout(() => {
			setPlayPanelLayoutAnimating(false);
			playPanelLayoutAnimTimeout = undefined;
		}, ms);
	};

	const resetPlayPanelState = () => {
		if (playPanelCloseTimeout !== undefined) {
			clearTimeout(playPanelCloseTimeout);
			playPanelCloseTimeout = undefined;
		}
		if (playPanelOpenFrame !== undefined) {
			cancelAnimationFrame(playPanelOpenFrame);
			playPanelOpenFrame = undefined;
		}
		clearPlayPanelLayoutAnimTimeout();
		setPlayPanelOpen(false);
		setPlayPanelClosing(false);
		setPlayPanelMounted(false);
		setPlayPanelLayoutAnimating(false);
		setVideoIndex(undefined);
		setClipToPlay(undefined);
	};

	const closePlayPanel = (options?: { immediate?: boolean }) => {
		if (playPanelOpenFrame !== undefined) {
			cancelAnimationFrame(playPanelOpenFrame);
			playPanelOpenFrame = undefined;
		}
		if (playPanelCloseTimeout !== undefined) {
			clearTimeout(playPanelCloseTimeout);
		}
		if (options?.immediate) {
			resetPlayPanelState();
			return;
		}
		beginPlayPanelLayoutAnimation();
		setPlayPanelClosing(true);
		playPanelCloseTimeout = setTimeout(() => {
			resetPlayPanelState();
		}, PLAY_PANEL_CLOSE_MS);
	};

	const setActiveVideoIndex = (index: number) => {
		setVideoIndex(index);
	};

	const openPlayPanel = (clip: BoardItemListItemResponse, index: number) => {
		if (playPanelCloseTimeout !== undefined) {
			clearTimeout(playPanelCloseTimeout);
			playPanelCloseTimeout = undefined;
		}
		setClipToPlay(clip);
		setCarouselInstantJumpToken((token) => token + 1);
		setVideoIndex(index);
		setPlayPanelClosing(false);
		setPlayPanelMounted(true);
		if (!playPanelOpen()) {
			beginPlayPanelLayoutAnimation();
			if (playPanelOpenFrame !== undefined) {
				cancelAnimationFrame(playPanelOpenFrame);
			}
			playPanelOpenFrame = requestAnimationFrame(() => {
				setPlayPanelOpen(true);
				playPanelOpenFrame = undefined;
				endPlayPanelLayoutAnimation(PLAY_PANEL_OPEN_MS);
			});
		}
	};

	const togglePlayPanel = () => {
		if (playPanelOpen()) {
			closePlayPanel();
			return;
		}
		beginPlayPanelLayoutAnimation();
		setPlayPanelMounted(true);
		playPanelOpenFrame = requestAnimationFrame(() => {
			setPlayPanelOpen(true);
			playPanelOpenFrame = undefined;
			endPlayPanelLayoutAnimation(PLAY_PANEL_OPEN_MS);
		});
	};

	const isPlayPanelActive = () => playPanelOpen() || playPanelMounted();

	onCleanup(() => {
		if (playPanelCloseTimeout !== undefined) {
			clearTimeout(playPanelCloseTimeout);
		}
		clearPlayPanelLayoutAnimTimeout();
		if (playPanelOpenFrame !== undefined) {
			cancelAnimationFrame(playPanelOpenFrame);
		}
	});

	return {
		playPanelMounted,
		playPanelOpen,
		playPanelClosing,
		playPanelLayoutAnimating,
		clipToPlay,
		videoIndex,
		carouselInstantJumpToken,
		openPlayPanel,
		setActiveVideoIndex,
		closePlayPanel,
		togglePlayPanel,
		isPlayPanelActive,
	};
}
